import React, { useEffect, useMemo, useState, useCallback } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import ExtraVideos from "../components/ExtraVideos";
import MainVideoSlot from "../components/MainVideoSlot";
import { Sparkles, Trash2, RefreshCw, AlertTriangle, Search, Megaphone, Copy, X as XIcon, Download, Plus, Pencil, Film, Upload as UploadIcon, Play, Building2, Camera, Briefcase, Eye, ShieldCheck, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import MuxPlayer from "@mux/mux-player-react";
import MuxUploader from "@mux/mux-uploader-react";
// Extracted subcomponents
import {
  EDIT_OPTIONS, PPV_OPTIONS, EXTRA_PPV_OPTIONS, OF_FREE_OPTIONS, RFC_OPTIONS,
} from "./videos/constants";
import {
  PlatformRow, CrewChips, PromoBlock, Sel, StatusComment, InlineEdit, Num, Stat,
} from "./videos/commonBits";
import VideoGrid from "./videos/VideoGrid";
import VideoPreviewModal from "./videos/VideoPreviewModal";
import MuxVideoModal from "./videos/MuxVideoModal";
import ManageVideoModal from "../components/manage-video/ManageVideoModal";
import CastCRMModal from "./videos/CastCRMModal";
import ScenesEditor from "./videos/ScenesEditor";
import CrmMultiSelect from "./videos/CrmMultiSelect";
import ScenePhotos from "./videos/ScenePhotos";
import MigrateStudiosButton from "./videos/MigrateStudiosButton";
import CSVImporter from "./Videos/CSVImporter";
import PromoPackV2Modal from "./videos/PromoPackV2Modal";

const PROMO_FAILURE_MESSAGE = "AI copy generation failed. Please press Regenerate.";
const PROMO_FAILURE_STATUS = "Promo pack created, but AI copy failed. Please press Regenerate.";
import OperationsPulse from "../components/OperationsPulse";
import {
  getLifecycleCounts,
  LIFECYCLE_FILTERS,
  matchesLifecycleFilter,
  matchesPlatformFilter,
  matchesPublishingFilter,
  PUBLISHING_FILTERS,
} from "./videos/productionReadiness";
import { useSearchParams } from "react-router-dom";



export default function Videos({ extraTabBar }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [query, setQuery] = useState("");
  const [publishingFilter, setPublishingFilter] = useState("all");
  const [lifecycleFilter, setLifecycleFilter] = useState("all");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [castFilter, setCastFilter] = useState("all");
  const [readinessFilter, setReadinessFilter] = useState("all");
  const [promo, setPromo] = useState(null); // { video, pack, busy, mood, progress, status }
  const [showCSVImporter, setShowCSVImporter] = useState(false);
  const [promoV2Video, setPromoV2Video] = useState(null); // Video selected for Promo Pack V2
  const [manageVideo, setManageVideo] = useState(null);
  const [previewVideo, setPreviewVideo] = useState(null);
  
  const getStatusLabel = (status) => {
    const labels = {
      'starting': 'Starting generation...',
      'processing': 'Processing video data...',
      'fetching_data': 'Loading video metadata...',
      'preparing': 'Preparing AI prompts...',
      'generating_safe': 'Generating IG/TikTok/Bluesky captions...',
      'generating_explicit': 'Generating X/OF/JFF/RFC captions...',
      'parsing': 'Finalizing results...',
      'complete': 'Complete!'
    };
    return labels[status] || 'Processing...';
  };
  
  const runPromo = async (video, mood = "auto") => {
    setPromo({ video, pack: null, busy: true, mood, progress: 0, status: "starting" });
    
    try {
      // Start generation
      const startRes = await api.post("/api/promo/pack", {
        video_id: video.id,
        mood,
        variation_seed: (crypto?.randomUUID?.() || String(Date.now())),
      });
      
      const jobId = startRes.data.job_id;
      
      // Poll for results
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await api.get(`/api/promo/status/${jobId}`);
          const { status, progress, result, error } = statusRes.data;
          
          // Update progress
          setPromo(prev => ({
            ...prev,
            progress: progress || 0,
            status: status
          }));
          
          // Check if complete
          if (status === 'complete') {
            clearInterval(pollInterval);
            setPromo({ video, pack: result, busy: false, mood, progress: 100, status: 'complete' });
            if (result?._ai_failed || result?._missing_platforms?.length) {
              toast.error(result?._ai_error || PROMO_FAILURE_MESSAGE);
            } else {
              toast.success('✓ Promo pack generated!');
            }
          } else if (status === 'error') {
            clearInterval(pollInterval);
            toast.error(`Generation failed: ${error || 'Unknown error'}`);
            setPromo(null);
          }
        } catch (pollErr) {
          console.error('Polling error:', pollErr);
          clearInterval(pollInterval);
          toast.error('Failed to check generation status');
          setPromo(null);
        }
      }, 3000); // Poll every 3 seconds
      
      // Timeout after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        if (promo?.status !== 'complete') {
          toast.error('Generation timed out');
          setPromo(null);
        }
      }, 300000);
      
    } catch (e) {
      const errMsg = e?.response?.data?.detail || "Promo gen failed";
      toast.error(errMsg);
      setPromo(null);
    }
  };
  const [muxModal, setMuxModal] = useState(null); // Add-video and preview flows

  const updateManagedVideo = useCallback((updated) => {
    if (!updated?.id) return;
    const mergeUpdate = (item) => {
      const merged = { ...item, ...updated };
      if (updated.platform_status) {
        merged.platform_status = { ...(item.platform_status || {}) };
        Object.entries(updated.platform_status).forEach(([key, value]) => {
          merged.platform_status[key] = { ...(merged.platform_status[key] || {}), ...value };
        });
      }
      return merged;
    };
    setManageVideo((current) => current?.video?.id === updated.id
      ? { ...current, video: mergeUpdate(current.video) }
      : current);
    setItems((current) => current.map((item) => item.id === updated.id ? mergeUpdate(item) : item));
  }, []);
  
  const [crmActors, setCrmActors] = useState([]);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("catalog_view") || "grid");
  useEffect(() => { localStorage.setItem("catalog_view", viewMode); }, [viewMode]);
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("catalog_sort") || "shoot_date_desc");
  useEffect(() => { localStorage.setItem("catalog_sort", sortBy); }, [sortBy]);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [thumbnailBatch, setThumbnailBatch] = useState(null);

  useEffect(() => {
    const requestedId = searchParams.get("videoId");
    if (!requestedId || !items.length) return;
    const video = items.find((item) => item.id === requestedId);
    if (video) {
      setManageVideo({ video, initialTab: "main" });
      setSearchParams((params) => { params.delete("videoId"); return params; }, { replace: true });
    }
  }, [items, searchParams, setSearchParams]);
  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} video${selectedIds.size === 1 ? "" : "s"}? This cannot be undone.`)) return;
    try {
      const r = await api.post("/api/videos/bulk-delete", { ids: Array.from(selectedIds) });
      toast.success(`✓ Deleted ${r.data.deleted_count} video${r.data.deleted_count === 1 ? "" : "s"}`);
      setSelectedIds(new Set());
      setSelectMode(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    }
  };

  const runThumbnailBatch = async (retryIds = null) => {
    const retrySet = retryIds ? new Set(retryIds) : null;
    const targets = retrySet ? items.filter((video) => retrySet.has(video.id)) : items;
    if (!targets.length || thumbnailBatch?.running) return;
    const action = retrySet ? "Retry thumbnail generation" : "Generate thumbnails";
    if (!window.confirm(`${action} for ${targets.length} video${targets.length === 1 ? "" : "s"}? Up to 30 unpublished Media Gallery images will be saved per ready video; existing generated images will be reused.`)) return;

    setThumbnailBatch({
      running: true,
      total: targets.length,
      completed: 0,
      processed: 0,
      skipped: 0,
      failed: 0,
      failedIds: [],
      results: [],
    });

    for (const video of targets) {
      let result;
      if (video.mux_status !== "ready") {
        result = { id: video.id, title: video.title, status: "skipped", message: "Mux video is not ready." };
      } else if (!video.mux_playback_id) {
        result = { id: video.id, title: video.title, status: "skipped", message: "No Mux playback source." };
      } else {
        try {
          const response = await api.post(`/api/mux/extract-thumbnails/${video.id}`, {}, { _noOfflineQueue: true });
          const count = response.data?.thumbnails?.length || 0;
          result = { id: video.id, title: video.title, status: "processed", message: `${count} thumbnails generated or reused.` };
        } catch (error) {
          const detail = error?.response?.data?.detail || error?.message || "Thumbnail extraction failed.";
          const skipped = error?.response?.status === 409 || /not ready|duration unknown|no mux playback/i.test(detail);
          result = { id: video.id, title: video.title, status: skipped ? "skipped" : "failed", message: detail };
        }
      }

      setThumbnailBatch((current) => ({
        ...current,
        completed: current.completed + 1,
        processed: current.processed + (result.status === "processed" ? 1 : 0),
        skipped: current.skipped + (result.status === "skipped" ? 1 : 0),
        failed: current.failed + (result.status === "failed" ? 1 : 0),
        failedIds: result.status === "failed" ? [...current.failedIds, video.id] : current.failedIds,
        results: [...current.results, result],
      }));
    }

    setThumbnailBatch((current) => ({ ...current, running: false }));
  };

  const load = useCallback(async () => {
    try {
      const [v, s, p, c] = await Promise.all([
        api.get("/api/videos/"),
        api.get("/api/videos/summary"),
        api.get("/api/platform-stats/summary"),
        api.get("/api/crm/?types=Model,Performer,Escort,Producer,Cameraman"),
      ]);
      setItems(v.data);
      setSummary(s.data);
      setPlatforms(p.data?.platforms || []);
      setCrmActors(c.data);
      console.log(`✓ Loaded ${v.data?.length || 0} videos`);
    } catch (e) {
      console.error("Load videos failed:", e);
      if (e?.response?.status === 401) {
        toast.error("Session expired - please login again");
      } else {
        toast.error(`Failed to load catalog: ${e?.response?.data?.detail || e.message}`);
      }
    }
  }, []);

  // AUTO-REFRESH: Listen for data changes
  useEffect(() => {
    const handleDataChange = (e) => {
      const { resourceType } = e.detail;
      if (resourceType === 'videos' || resourceType === 'catalog') {
        setTimeout(load, 100);
      }
    };
    
    window.addEventListener('gio:data-changed', handleDataChange);
    return () => window.removeEventListener('gio:data-changed', handleDataChange);
  }, [load]);

  // INITIAL LOAD on mount
  useEffect(() => {
    load();
  }, [load]);

  const seed = async (force = false) => {
    try {
      const r = await api.post(`/api/videos/${force ? "seed-force" : "seed"}`);
      toast.success(`Seeded ${r.data.seeded} videos`);
      load();
    } catch (e) {
      if (e?.response?.status === 409) {
        if (window.confirm(e.response.data.detail + "\n\nReset and re-seed?")) seed(true);
      } else toast.error(e?.response?.data?.detail || "Seed failed");
    }
  };

  const patch = async (id, field, value) => {
    try {
      const body = { [field]: value };
      await api.put(`/api/videos/${id}`, body);
      // refresh only this row via full reload (simpler, small list)
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this video entry?")) return;
    await api.delete(`/api/videos/${id}`);
    load();
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    const out = items.filter((v) => {
      const hasLegal = (v.legal_docs || []).length > 0 || Boolean(v.legal_notes || v.studio_contract_url);
      if (!matchesLifecycleFilter(v, lifecycleFilter)) return false;
      if (!matchesPublishingFilter(v, publishingFilter)) return false;
      if (!matchesPlatformFilter(v, platformFilter)) return false;
      if (cityFilter !== "all" && (v.city || "Unassigned") !== cityFilter) return false;
      if (castFilter !== "all" && !(v.cast_crm_ids || []).includes(castFilter) && !(v.cast || "").toLowerCase().includes(castFilter.toLowerCase())) return false;
      if (readinessFilter === "legal_missing" && !(v.edit_status === "no_publishing" && !hasLegal)) return false;
      if (readinessFilter === "legal_on_file" && !hasLegal) return false;
      
      if (!q) return true;
      return (v.title + " " + v.cast + " " + (v.tags || []).join(" ") + " " + v.city + " " + (v.studio_name_cache || "")).toLowerCase().includes(q);
    });

    // Sort
    const PLATFORM_DATES = ["jff_posted_at","of_vip_posted_at","of_free_posted_at","rfc_posted_at","faphouse_posted_at","top4fans_posted_at","darkfans_posted_at"];
    const latestPosted = (v) => {
      const ds = PLATFORM_DATES.map(k => v[k]).filter(Boolean).sort();
      return ds[ds.length - 1] || "";
    };
    const cmpAsc = (a, b) => (a || "").localeCompare(b || "");
    const cmpDesc = (a, b) => (b || "").localeCompare(a || "");
    const sorted = [...out];
    switch (sortBy) {
      case "shoot_date_desc": sorted.sort((a, b) => cmpDesc(a.shoot_date, b.shoot_date)); break;
      case "shoot_date_asc":  sorted.sort((a, b) => cmpAsc(a.shoot_date, b.shoot_date));  break;
      case "created_desc":    sorted.sort((a, b) => cmpDesc(a.created_at, b.created_at)); break;
      case "created_asc":     sorted.sort((a, b) => cmpAsc(a.created_at, b.created_at));  break;
      case "upload_date_desc": sorted.sort((a, b) => cmpDesc(a.upload_date, b.upload_date)); break;
      case "upload_date_asc":  sorted.sort((a, b) => cmpAsc(a.upload_date, b.upload_date));  break;
      case "posted_desc":     sorted.sort((a, b) => cmpDesc(latestPosted(a), latestPosted(b))); break;
      case "posted_asc":      sorted.sort((a, b) => cmpAsc(latestPosted(a), latestPosted(b)));  break;
      case "title_asc":       sorted.sort((a, b) => cmpAsc(a.title, b.title)); break;
      default: break;
    }
    return sorted;
  }, [items, query, publishingFilter, lifecycleFilter, sortBy, platformFilter, cityFilter, castFilter, readinessFilter]);

  const productionStats = useMemo(() => getLifecycleCounts(items), [items]);
  const publishingCounts = useMemo(() => Object.fromEntries(
    PUBLISHING_FILTERS.map(({ value }) => [value, value === "all" ? items.length : items.filter((video) => matchesPublishingFilter(video, value)).length]),
  ), [items]);

  const cities = useMemo(() => [...new Set(items.map(v => v.city || "Unassigned"))].sort(), [items]);
  const casts = useMemo(() => {
    const map = new Map();
    crmActors.forEach(actor => map.set(actor.id, actor.stage_name || actor.legal_name || actor.id));
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [crmActors]);

  return (
    <AppLayout title="Video library" overline="150 · MASTER CATALOG · PER-PLATFORM TRACKING">
      {extraTabBar}
      {/* Summary - Neon Theme */}
      <OperationsPulse
        title="Production command center"
        subtitle="Separate lifecycle readiness from platform publishing so blocked work is visible before release decisions."
        accent="#a855f7"
        metrics={[
          { label: "Catalog", value: productionStats.total, detail: "tracked videos" },
          { label: "Ready", value: productionStats.ready, color: "#22c55e", detail: "lifecycle" },
          { label: "Editing", value: productionStats.editing, color: "#f59e0b", detail: "in progress" },
          { label: "Blocked", value: productionStats.blocked, color: "#ef4444", detail: "no publish / legal" },
        ]}
        gap="⚠️ Limited: Performance metrics and hub-attributed conversion data shown only for linked content."
      />
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 fade-up" data-testid="videos-summary">
        <Stat label="Total" value={summary?.total ?? 0} testid="v-sum-total" />
        <Stat label="Needs edit" value={summary?.needs_edit ?? 0} accent testid="v-sum-needs" />
        <Stat label="Live on platforms" value={summary?.live_total ?? 0} testid="v-sum-live" />
        <Stat label="Cities" value={Object.keys(summary?.cities || {}).length} testid="v-sum-cities" />
      </section>

      {/* Catalog actions */}
      <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="videos-toolbar">
        <div className="flex items-center gap-2 border border-white/10 bg-black/80 backdrop-blur-xl px-3 py-2 flex-1 min-w-[240px]">
          <Search size={14} className="text-zinc-500" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search title, cast, tag, city..."
            data-testid="videos-search"
            className="bg-transparent text-white text-sm focus:outline-none flex-1 placeholder-zinc-500" />
        </div>
        {/* Sort dropdown */}
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
          data-testid="videos-sort"
          className="px-3 py-2 text-[11px] font-mono-ui uppercase tracking-wider border bg-black/80 backdrop-blur-xl text-zinc-300 border-white/10 hover:text-white focus:outline-none focus:border-purple-500 transition"
          title="Sort by">
          <option value="shoot_date_desc">📅 Shoot date · newest</option>
          <option value="shoot_date_asc">📅 Shoot date · oldest</option>
          <option value="upload_date_desc">⬆️ Upload date · newest</option>
          <option value="upload_date_asc">⬆️ Upload date · oldest</option>
          <option value="created_desc">➕ Created · newest</option>
          <option value="created_asc">➕ Created · oldest</option>
          <option value="posted_desc">🚀 Last posted · newest</option>
          <option value="posted_asc">🚀 Last posted · oldest</option>
          <option value="title_asc">🔤 Title A→Z</option>
        </select>
        {/* Grid view only - table view removed */}
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-mono-ui uppercase tracking-wider text-zinc-500">▦ GRID</span>
        </div>
        <button onClick={() => { setSelectMode(m => !m); setSelectedIds(new Set()); }}
          data-testid="videos-select-mode"
          className={`px-3 py-2 text-[11px] font-mono-ui uppercase tracking-wider border transition ${selectMode ? "bg-purple-500 text-black border-purple-500" : "border-white/10 text-zinc-400 hover:text-white hover:border-white/20"}`}>
          {selectMode ? "✕ Exit select" : "✓ Select"}
        </button>
        <button type="button" onClick={() => runThumbnailBatch()} disabled={!items.length || thumbnailBatch?.running}
          data-testid="videos-bulk-thumbnails" className="btn-ghost inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50">
          <Camera size={14} />{thumbnailBatch?.running ? "Processing thumbnails..." : "Generate thumbnails for ready videos"}
        </button>
        {selectMode && selectedIds.size > 0 && (
          <>
            <button onClick={() => setSelectedIds(new Set(filtered.map(v => v.id)))}
              data-testid="videos-select-all"
              className="btn-ghost text-[11px]">
              Select all ({filtered.length})
            </button>
            <button onClick={bulkDelete}
              data-testid="videos-bulk-delete"
              className="px-3 py-2 text-[11px] font-mono-ui uppercase tracking-wider bg-red-950/50 border border-red-500/50 text-red-300 hover:bg-red-900/50 hover:border-red-500 transition backdrop-blur-xl">
              Delete {selectedIds.size}
            </button>
          </>
        )}
        <button onClick={() => seed(false)} data-testid="videos-seed-button"
          className="btn-ghost inline-flex items-center gap-2 text-purple-400 hover:text-purple-300"><Sparkles size={14} /> Seed catalog</button>
        <button onClick={() => seed(true)} data-testid="videos-reseed-button"
          className="btn-ghost inline-flex items-center gap-2 text-purple-400 hover:text-purple-300" title="Safe top-up: adds missing seed videos, preserves Mux uploads/edits/revenue/custom"><RefreshCw size={14} /></button>
        <MigrateStudiosButton onDone={load} />
        <button onClick={() => {
          const token = localStorage.getItem("gio_token");
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/exports/videos.csv`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.blob()).then(blob => {
              const u = URL.createObjectURL(blob); const a = document.createElement("a");
              a.href = u; a.download = "gioagent_videos.csv"; a.click(); URL.revokeObjectURL(u);
            });
        }} data-testid="videos-export-button" className="btn-ghost inline-flex items-center gap-2"><Download size={14} /> CSV</button>
        <button 
          onClick={() => setShowCSVImporter(true)}
          data-testid="videos-import-csv-button" 
          className="btn-ghost inline-flex items-center gap-2"
        >
          <UploadIcon size={14} /> Import CSV
        </button>
        <button 
          onClick={() => {
            // Create new video via modal
            const newVideo = {
              id: null,
              title: "",
              cast: "",
              city: "",
              location: "",
              shoot_date: "",
              runtime_minutes: "",
              edit_status: "pending",
              tags: [],
              is_studio: false,
              description: "",
            };
            setManageVideo({ video: newVideo, createMode: true, initialTab: "main" });
          }}
          data-testid="videos-add-button" 
          className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-semibold rounded-lg transition-all inline-flex items-center gap-2 shadow-lg shadow-purple-500/30"
        >
          <Plus size={16} /> Add video
        </button>
      </div>
      {thumbnailBatch && (
        <section data-testid="videos-thumbnail-batch" aria-live="polite" className="mb-5 rounded-lg border border-cyan-500/25 bg-zinc-950/70 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-white">Thumbnail batch {thumbnailBatch.running ? "in progress" : "complete"}</h2>
              <p className="mt-1 text-xs text-zinc-400">
                Progress {thumbnailBatch.completed}/{thumbnailBatch.total} · Processed {thumbnailBatch.processed} · Skipped {thumbnailBatch.skipped} · Failed {thumbnailBatch.failed}
              </p>
            </div>
            {!thumbnailBatch.running && thumbnailBatch.failedIds.length > 0 && (
              <button type="button" data-testid="videos-retry-thumbnail-failures" className="btn-ghost inline-flex items-center gap-2"
                onClick={() => runThumbnailBatch(thumbnailBatch.failedIds)}>
                <RefreshCw size={14} />Retry failed ({thumbnailBatch.failedIds.length})
              </button>
            )}
          </div>
          <div role="progressbar" aria-label="Thumbnail batch progress" aria-valuemin={0} aria-valuemax={thumbnailBatch.total}
            aria-valuenow={thumbnailBatch.completed} className="mt-3 h-1.5 overflow-hidden rounded bg-white/10">
            <div className="h-full bg-cyan-400 transition-[width]" style={{ width: `${thumbnailBatch.total ? (thumbnailBatch.completed / thumbnailBatch.total) * 100 : 100}%` }} />
          </div>
          <ul className="mt-3 max-h-40 space-y-1 overflow-y-auto text-xs" data-testid="videos-thumbnail-results">
            {thumbnailBatch.results.map((result) => (
              <li key={result.id} className={result.status === "failed" ? "text-red-300" : result.status === "skipped" ? "text-zinc-400" : "text-emerald-300"}>
                {result.title || result.id}: {result.message}
              </li>
            ))}
          </ul>
        </section>
      )}
      <div className="mb-4 space-y-3" data-testid="videos-status-filters">
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Lifecycle status">
          <span className="w-28 text-[11px] font-mono-ui uppercase tracking-wider text-zinc-500">Lifecycle</span>
          {LIFECYCLE_FILTERS.map(({ value, label, count }) => (
            <button key={value} type="button" onClick={() => setLifecycleFilter(value)} aria-pressed={lifecycleFilter === value}
              data-testid={`filter-lifecycle-${value}`}
              className={`rounded border px-3 py-2 text-[11px] font-mono-ui uppercase tracking-wider transition ${lifecycleFilter === value ? "border-emerald-500/70 bg-emerald-950/40 text-emerald-300" : "border-zinc-700 text-zinc-400 hover:border-emerald-500/50 hover:text-white"}`}>
              {label} <span className="ml-1 tabular-nums text-zinc-500">{productionStats[count]}</span>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Platform publishing status">
          <span className="w-28 text-[11px] font-mono-ui uppercase tracking-wider text-zinc-500">Publishing</span>
          {PUBLISHING_FILTERS.map(({ value, label }) => (
            <button key={value} type="button" onClick={() => setPublishingFilter(value)} aria-pressed={publishingFilter === value}
              data-testid={`filter-publishing-${value}`}
              className={`rounded border px-3 py-2 text-[11px] font-mono-ui uppercase tracking-wider transition ${publishingFilter === value ? "border-sky-500/70 bg-sky-950/40 text-sky-300" : "border-zinc-700 text-zinc-400 hover:border-sky-500/50 hover:text-white"}`}>
              {label} <span className="ml-1 tabular-nums text-zinc-500">{publishingCounts[value]}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="videos-operations-filters">
        <select value={platformFilter} onChange={e => setPlatformFilter(e.target.value)} className="px-3 py-2 text-[11px] font-mono-ui uppercase tracking-wider border bg-black/80 text-zinc-300 border-white/10">
          <option value="all">All platform status</option>{platforms.map(p => <option key={p.platform || p.id} value={p.platform || p.id}>{p.display_name || p.platform || p.id}</option>)}
        </select>
        <select value={cityFilter} onChange={e => setCityFilter(e.target.value)} className="px-3 py-2 text-[11px] font-mono-ui uppercase tracking-wider border bg-black/80 text-zinc-300 border-white/10">
          <option value="all">All cities</option>{cities.map(city => <option key={city} value={city}>{city}</option>)}
        </select>
        <select value={castFilter} onChange={e => setCastFilter(e.target.value)} className="px-3 py-2 text-[11px] font-mono-ui uppercase tracking-wider border bg-black/80 text-zinc-300 border-white/10">
          <option value="all">All cast</option>{casts.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <select aria-label="Legal status" value={readinessFilter} onChange={e => setReadinessFilter(e.target.value)} className="px-3 py-2 text-[11px] font-mono-ui uppercase tracking-wider border bg-black/80 text-zinc-300 border-white/10">
          <option value="all">Any legal status</option><option value="legal_missing">Legal missing</option><option value="legal_on_file">Legal on file</option>
        </select>
        <span className="text-[11px] text-zinc-500">{filtered.length} shown · {productionStats.platformReady} with scheduled or live destinations</span>
      </div>
      <VideoGrid
        videos={filtered}
        crmActors={crmActors}
        selectMode={selectMode}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onDelete={remove}
        onPromo={runPromo}
        onPromoV2={(v) => setPromoV2Video(v)}
        onPreview={setPreviewVideo}
        onEdit={(v) => setManageVideo({ video: v, initialTab: "main" })}
        onPlay={setPreviewVideo}
      />
      {previewVideo && <VideoPreviewModal video={previewVideo} crmActors={crmActors} onClose={() => setPreviewVideo(null)} />}
      {manageVideo && (
        <ManageVideoModal
          videoId={manageVideo.video?.id}
          video={manageVideo.video}
          createMode={Boolean(manageVideo.createMode)}
          onCreated={(created) => {
            setItems((current) => current.some((item) => item.id === created.id) ? current.map((item) => item.id === created.id ? created : item) : [created, ...current]);
            setManageVideo((current) => current ? { ...current, createMode: false, video: created } : current);
          }}
          initialTab={manageVideo.initialTab || "main"}
          onClose={() => setManageVideo(null)}
          onChange={updateManagedVideo}
        />
      )}
      {muxModal && (
        <MuxVideoModal
          video={muxModal.video}
          initialView={muxModal.initialView || "edit"}
          onClose={() => setMuxModal(null)}
          onChange={(updated) => {
            setMuxModal((prev) => (prev && prev.video?.id === updated?.id ? { ...prev, video: { ...prev.video, ...updated } } : prev));
            if (updated.id && items.find(i => i.id === updated.id)) {
              // Update existing
              setItems((prev) =>
                prev.map((item) =>
                  item.id === updated.id ? { ...item, ...updated } : item
                )
              );
            } else if (updated.id && !items.find(i => i.id === updated.id)) {
              // New video created - add to list
              setItems((prev) => [updated, ...prev]);
            }
          }}
        />
      )}
      {/* PromoPack Modal - Neon Theme */}
      {promo && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-4" onClick={() => !promo.busy && setPromo(null)}>
          <div className="absolute inset-0 bg-black/90 backdrop-blur-md" />
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-t-2xl md:rounded-2xl bg-black/95 backdrop-blur-xl border border-white/10"
            data-testid="promo-modal">
            <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-white/10 bg-black/95 backdrop-blur-xl">
              <div>
                <div className="text-xs font-bold uppercase mb-1 text-purple-400">
                  <Megaphone size={14} className="inline mr-1" /> PROMO PACK
                </div>
                <h2 className="text-xl font-bold text-white">{promo.video.title}</h2>
              </div>
              <button onClick={() => setPromo(null)} className="p-2 rounded hover:bg-zinc-800/50 text-zinc-400 hover:text-white transition">
                <XIcon size={24} />
              </button>
            </div>
            <div className="p-6">
              {promo.busy ? (
                <div className="flex flex-col items-center gap-4 py-12">
                  <RefreshCw size={32} className="animate-spin text-purple-500" />
                  
                  {/* Progress Bar */}
                  <div className="w-full max-w-md px-4">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-white">{getStatusLabel(promo.status)}</span>
                      <span className="text-zinc-400">{promo.progress}%</span>
                    </div>
                    <div className="h-2 bg-zinc-800/50 rounded-full overflow-hidden backdrop-blur-xl">
                      <div 
                        className="h-full bg-gradient-to-r from-purple-600 to-pink-600 transition-all duration-500"
                        style={{ width: `${promo.progress}%` }}
                      />
                    </div>
                  </div>
                  
                  <p className="text-zinc-400 text-sm">
                    This may take 30-60 seconds...
                  </p>
                </div>
              ) : promo.pack ? (
                <>
                  <PromoMoodPicker
                    current={promo.mood}
                    busy={promo.busy}
                    onPick={(mood) => runPromo(promo.video, mood)}
                    onRegenerate={() => runPromo(promo.video, promo.mood)}
                  />
                  {(promo.pack?._ai_failed || promo.pack?._missing_platforms?.length) && (
                    <p role="alert" className="mt-4 rounded-lg border border-amber-700/50 bg-amber-900/20 p-3 text-sm text-amber-200">
                      {PROMO_FAILURE_STATUS}
                    </p>
                  )}
                  <div className="mt-6">
                    <PromoPackView pack={promo.pack} video={promo.video} />
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <p className="text-zinc-400">Failed to generate promo pack</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* CSV Importer Modal */}
      {showCSVImporter && (
        <CSVImporter 
          onClose={() => setShowCSVImporter(false)}
          onSuccess={() => {
            setShowCSVImporter(false);
            load(); // Reload catalog after import
          }}
        />
      )}

      {/* Promo Pack V2 Modal */}
      <PromoPackV2Modal
        show={!!promoV2Video}
        onClose={() => setPromoV2Video(null)}
        video={promoV2Video}
      />
    </AppLayout>
  );
}


// ─────────────────────────────────────────────────────────
// Promo Mood picker — chips + regenerate button.
// Selecting a new mood immediately regenerates the pack.
// ─────────────────────────────────────────────────────────
const PROMO_MOODS = [
  { key: "auto",        label: "AUTO",        emoji: "✨" },
  { key: "alpha",       label: "ALPHA",       emoji: "🔥" },
  { key: "filthy",      label: "FILTHY",      emoji: "😈" },
  { key: "playful",     label: "PLAYFUL",     emoji: "😏" },
  { key: "seductive",   label: "SEDUCTIVE",   emoji: "🥵" },
  { key: "provocative", label: "PROVOCATIVE", emoji: "💥" },
  { key: "story",       label: "STORY",       emoji: "💭" },
  { key: "romantic",    label: "ROMANTIC",    emoji: "💗" },
  { key: "bts",         label: "BTS",         emoji: "🎬" },
];

function PromoMoodPicker({ current, busy, onPick, onRegenerate }) {
  return (
    <div className="mt-4" data-testid="promo-mood-picker">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="overline text-zinc-500 text-[10px]">CHANGE MOOD · regenerates with different variety</div>
        <button
          onClick={onRegenerate}
          disabled={busy}
          data-testid="promo-regenerate"
          className="inline-flex items-center gap-1.5 px-2.5 py-1 border text-[10px] font-mono-ui uppercase tracking-wider transition disabled:opacity-40 border-purple-400 text-purple-400 hover:bg-purple-500/10"
          title="Regenerate with same mood but fresh seed">
          <RefreshCw size={11} className={busy ? "animate-spin" : ""} /> Regenerate
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {PROMO_MOODS.map((m) => {
          const active = current === m.key;
          return (
            <button key={m.key}
              onClick={() => onPick(m.key)}
              disabled={busy}
              data-testid={`promo-mood-${m.key}`}
              className={`inline-flex items-center gap-1 px-2.5 py-1 border text-[10px] font-mono-ui uppercase tracking-wider transition disabled:opacity-40 ${
                active 
                  ? "border-purple-500 text-purple-400 bg-purple-500/10" 
                  : "border-white/10 text-zinc-400 hover:border-white/20"
              }`}>
              <span>{m.emoji}</span> {m.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────
// Promo Pack viewer — 7 platforms × 3 variations (short / medium / long with CTA)
// Replaces the legacy single-caption-per-platform layout.
// ─────────────────────────────────────────────────────────
const PROMO_PLATFORMS = [
  { key: "instagram",    label: "📸 INSTAGRAM",    tid: "promo-ig" },
  { key: "tiktok",       label: "🎵 TIKTOK",       tid: "promo-tiktok" },
  { key: "twitter",      label: "𝕏 X / TWITTER",  tid: "promo-twitter" },
  { key: "onlyfans",     label: "🔞 ONLYFANS",     tid: "promo-of" },
  { key: "justforfans",  label: "🍑 JUSTFORFANS",  tid: "promo-jff" },
  { key: "bluesky",      label: "🦋 BLUESKY",      tid: "promo-bsky" },
  { key: "rfc",          label: "🔥 RFC",          tid: "promo-rfc" },
  { key: "faphouse",     label: "🏠 FAPHOUSE",     tid: "promo-faphouse" },
  { key: "store",        label: "🛒 STORE",        tid: "promo-store" },
  { key: "members_area", label: "🔐 MEMBERS",      tid: "promo-members" },
  { key: "vip_tier",     label: "👑 VIP TIER",     tid: "promo-vip" },
];

function PromoPackView({ pack, video }) {
  const [tab, setTab] = React.useState("instagram");
  const [saving, setSaving] = React.useState(false);
  
  const active = pack[tab] || {};
  const variations = ["short", "medium", "long"].map((style) => {
    const v = active[style];
    if (!v) return null;
    if (typeof v === "string") return { style, text: v, hashtags: [] };
    return {
      style,
      text: v.text || v.caption || v.post || "",
      hashtags: Array.isArray(v.hashtags) ? v.hashtags : [],
    };
  }).filter(Boolean);
  
  // Save selected variation as store description
  const saveToStore = async (text) => {
    if (!video || !video.id) {
      toast.error("No video context available");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/api/videos/${video.id}`, {
        store_description: text,
      });
      toast.success("✓ Saved to Video Store description");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="promo-pack-view">
      {/* Platform tabs */}
      <div className="flex flex-wrap gap-1.5 border-b border-white/10 pb-3" data-testid="promo-platform-tabs">
        {PROMO_PLATFORMS.map((p) => (
          <button key={p.key} onClick={() => setTab(p.key)} data-testid={`promo-tab-${p.key}`}
            className={`px-3 py-1.5 text-[11px] font-mono-ui uppercase tracking-wider border transition ${
              tab === p.key 
                ? "border-purple-500 text-purple-400 bg-purple-500/10" 
                : "border-white/10 text-zinc-400 hover:border-white/20"
            }`}>
            {p.label}
          </button>
        ))}
      </div>

      {/* 3 variations for the selected platform */}
      {variations.length === 0 ? (
        <div className="border border-dashed border-white/10 p-8 text-center text-zinc-500 text-sm rounded-lg bg-black/50 backdrop-blur-xl" data-testid="promo-empty-platform">
          No copy generated for this platform.
        </div>
      ) : (
        <div className="space-y-3" data-testid={`promo-variations-${tab}`}>
          {variations.map((v) => (
            <PromoVariation 
              key={v.style} 
              style={v.style} 
              text={v.text} 
              hashtags={v.hashtags} 
              platform={tab}
              onSaveToStore={saveToStore}
              savingToStore={saving}
            />
          ))}
        </div>
      )}

      {/* DM blast + TheBlowers tweet shown below all platforms (always visible) */}
      <div className="pt-4 mt-4 border-t border-white/10 space-y-3" data-testid="promo-extras">
        {(() => {
          const dm = typeof pack.dm_blast === "string" ? pack.dm_blast : (pack.dm_blast?.body || "");
          return dm ? <PromoBlock title="💬 DM BLAST" text={dm} tid="promo-dmblast" /> : null;
        })()}
        {pack.theblowers_tweet && (
          <PromoBlock title="💨 THEBLOWERS AFFILIATE TWEET" text={pack.theblowers_tweet} tid="promo-blowers" accent />
        )}
        {pack.posting_tip && (
          <div className="overline text-purple-400" data-testid="promo-tip">💡 TIP: {pack.posting_tip}</div>
        )}
      </div>
    </div>
  );
}

function PromoVariation({ style, text, hashtags, platform, onSaveToStore, savingToStore }) {
  const fullText = (hashtags && hashtags.length > 0) ? `${text}\n\n${hashtags.join(" ")}` : text;
  const copyAll = () => {
    navigator.clipboard.writeText(fullText);
    toast.success(`${style.toUpperCase()} copied`);
  };
  const scheduleIt = async () => {
    // Copy text first as fallback
    navigator.clipboard.writeText(fullText);
    if (platform === "twitter") {
      // X composer prefill — truncate to 280 chars for safety
      const tweet = fullText.length > 280 ? fullText.slice(0, 277) + "…" : fullText;
      window.open(`/x?prefill=${encodeURIComponent(tweet)}`, "_self");
      return;
    }
    if (platform === "onlyfans") {
      // Queue in OF scheduler — default 1h from now
      const dt = new Date(Date.now() + 60 * 60 * 1000).toISOString();
      try {
        await api.post("/api/onlyfans/posts", { text: fullText, scheduled_at: dt, price: 0, slot: "default" });
        toast.success("📅 Queued in OnlyFans scheduler (+1h) — edit in /onlyfans");
      } catch (e) { toast.error(e?.response?.data?.detail || "OF queue failed"); }
      return;
    }
    // For all other platforms (IG, TikTok, JFF, Bluesky, RFC):
    // log to Multi Tracker as a planned (draft) post
    const platformMap = {
      instagram: "instagram",
      tiktok: "tiktok",
      bluesky: "bluesky",
      justforfans: "justforfans",
      rfc: "rawfuckclub",
    };
    const targetPlatform = platformMap[platform] || "other";
    try {
      await api.post("/api/social-posts/", {
        platform: targetPlatform,
        post_url: "",
        caption: fullText,
        media_kind: "image",
        image_urls: [],
        posted_at: new Date().toISOString().slice(0, 10),
        status: "draft",
        tags: [],
        notes: `Drafted from PromoPack (${style})`,
      });
      toast.success(`📋 Added to Multi-Tracker as draft (${targetPlatform})`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save to tracker failed"); }
  };
  const accent =
    style === "short" ? "text-zinc-400" :
    style === "medium" ? "text-purple-400" :
    "text-pink-400";
  const badge =
    style === "short" ? "≤ 80 chars" :
    style === "medium" ? "~150-260 chars" :
    "350-500 chars · CTA";
  const scheduleLabel =
    platform === "twitter" ? "→ X Composer" :
    platform === "onlyfans" ? "Queue in OF" :
    "Add to Tracker";
  return (
    <div className="border border-white/10 bg-black/80 backdrop-blur-xl p-4 rounded-lg" data-testid={`promo-${platform}-${style}`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className={`overline ${accent}`}>{style}</span>
          <span className="text-[10px] font-mono-ui text-zinc-500">{badge}</span>
          <span className="text-[10px] font-mono-ui text-zinc-500">· {text.length} chars</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={scheduleIt} data-testid={`promo-${platform}-${style}-schedule`}
            className="text-purple-400 hover:text-purple-300 text-xs font-mono-ui uppercase tracking-wider flex items-center gap-1 transition">
            📅 {scheduleLabel}
          </button>
          <button onClick={copyAll} data-testid={`promo-${platform}-${style}-copy`}
            className="text-zinc-400 hover:text-purple-400 text-xs font-mono-ui uppercase tracking-wider flex items-center gap-1 transition">
            <Copy size={12} /> Copy
          </button>
          {onSaveToStore && (
            <button 
              onClick={() => onSaveToStore(fullText)} 
              disabled={savingToStore}
              className="text-pink-400 hover:text-pink-300 text-xs font-mono-ui uppercase tracking-wider flex items-center gap-1 disabled:opacity-40 transition"
              title="Save this caption as Video Store description"
            >
              🛒 {savingToStore ? "Saving..." : "→ Store"}
            </button>
          )}
        </div>
      </div>
      <div className="text-white text-sm whitespace-pre-wrap leading-relaxed mb-3">{text}</div>
      {hashtags && hashtags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {hashtags.map((h, i) => (
            <span key={i} className="text-[11px] font-mono-ui text-zinc-400 border border-white/10 px-2 py-0.5 rounded">{h}</span>
          ))}
        </div>
      )}
    </div>
  );
}
